/**
 * Admin → Business Units
 *
 * Phase 5b item 3 (BusinessUnits page). Lists / creates / edits / archives
 * business units for the active tenant. Renders source badges so operators
 * can tell at a glance which BUs were imported from FOCTTA core vs created
 * locally.
 *
 * Re-sync button (top-right) refreshes Core-sourced BUs additively - never
 * overwrites or deletes existing rows (per the 2026-05-15 locked decision).
 *
 * The system-reserved __unassigned__ BU is rendered with an explicit badge
 * and cannot be edited or archived (server enforces; UI disables the
 * buttons so operators get a predictable no-op).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BundleChips,
  PackOriginChips,
  humanisePackId,
  resolutionSentence,
} from '@/components/industry/PackProvenance';
import { motion } from 'motion/react';
import {
  Building2,
  Plus,
  Pencil,
  Archive as ArchiveIcon,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Link as LinkIcon,
  Home,
  ShieldCheck,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/primitives';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import {
  listBusinessUnits,
  createBusinessUnit,
  updateBusinessUnit,
  archiveBusinessUnit,
  resyncBusinessUnitsFromFocttaCore,
  type BusinessUnit,
  type BusinessUnitSource,
  type CreateBusinessUnitInput,
  type UpdateBusinessUnitInput,
  type ResyncFromCoreResponse,
} from '@/lib/api';

type SourceFilter = BusinessUnitSource | 'all';

export function AdminBusinessUnits(): JSX.Element {
  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [showArchived, setShowArchived] = useState(false);
  const [resyncResult, setResyncResult] = useState<ResyncFromCoreResponse | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createInput, setCreateInput] = useState<CreateBusinessUnitInput>({ name: '' });

  const [editTarget, setEditTarget] = useState<BusinessUnit | null>(null);
  const [editInput, setEditInput] = useState<UpdateBusinessUnitInput>({});

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await listBusinessUnits(
        sourceFilter === 'all' ? { withIndustry: true } : { source: sourceFilter, withIndustry: true },
      );
      setBusinessUnits(result.businessUnits);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load business units');
    } finally {
      setLoading(false);
    }
  }, [sourceFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const visibleRows = useMemo(() => {
    return showArchived
      ? businessUnits
      : businessUnits.filter((b) => b.status !== 'archived');
  }, [businessUnits, showArchived]);

  // Map of id -> name so we can show parent labels in rows + parent dropdown.
  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const b of businessUnits) m.set(b.id, b.name);
    return m;
  }, [businessUnits]);

  // Counts follow what the list shows: an archived unit is hidden until "Show
  // archived", so counting it made the header say "4 from FOCTTA core" above
  // three rows (replica proof, 21 Sep 2026).
  const sourceCounts = useMemo(() => {
    const counts: Record<BusinessUnitSource, number> = {
      local: 0,
      foctta_core: 0,
      system: 0,
    };
    for (const b of visibleRows) counts[b.source] += 1;
    return counts;
  }, [visibleRows]);

  async function handleCreate(): Promise<void> {
    if (!createInput.name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await createBusinessUnit({
        name: createInput.name.trim(),
        slug: createInput.slug?.trim() || undefined,
        parentId: createInput.parentId ?? null,
        description: createInput.description?.trim() || null,
      });
      setCreateOpen(false);
      setCreateInput({ name: '' });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to create business unit');
    } finally {
      setBusy(false);
    }
  }

  async function handleUpdate(): Promise<void> {
    if (editTarget == null) return;
    setBusy(true);
    setError(null);
    try {
      await updateBusinessUnit(editTarget.id, editInput);
      setEditTarget(null);
      setEditInput({});
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to update business unit');
    } finally {
      setBusy(false);
    }
  }

  async function handleArchive(bu: BusinessUnit): Promise<void> {
    if (
      !window.confirm(
        `Archive business unit "${bu.name}"? It will be hidden from default lists but can be restored later. Historical findings/events/decisions remain attributed to it.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await archiveBusinessUnit(bu.id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to archive business unit');
    } finally {
      setBusy(false);
    }
  }

  async function handleResync(): Promise<void> {
    setBusy(true);
    setError(null);
    setResyncResult(null);
    try {
      const result = await resyncBusinessUnitsFromFocttaCore();
      setResyncResult(result);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to re-sync from FOCTTA core');
    } finally {
      setBusy(false);
    }
  }

  function startEdit(bu: BusinessUnit): void {
    setEditTarget(bu);
    setEditInput({
      name: bu.name,
      slug: bu.slug,
      parentId: bu.parentId,
      description: bu.description,
      primaryOwnerUserId: bu.primaryOwnerUserId,
      status: bu.status === 'archived' ? 'archived' : 'active',
    });
  }

  return (
    <PageContainer>
      <PageHeader
        title="Admin · Business Units"
        subtitle="Manage business units for tenant scoping. Discovery's findings, activity events, and detection decisions can all be filtered by BU. Bootstrap-imported BUs from FOCTTA core appear here automatically."
        badges={[
          { label: `${sourceCounts.local} local`, variant: 'glass' },
          { label: `${sourceCounts.foctta_core} from FOCTTA core`, variant: 'info' },
          { label: `${sourceCounts.system} system`, variant: 'warning' },
        ]}
      />

      {error && (
        <GlassCard className="p-4 mb-4 border-destructive">
          <p className="text-xs text-destructive">{error}</p>
        </GlassCard>
      )}

      {resyncResult && (
        <GlassCard className="p-4 mb-4 border-primary glow-low">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <div className="flex-1 text-xs">
              <p>{resyncResult.message}</p>
              {resyncResult.sourceEventId && (
                <p className="text-muted-foreground mt-1">
                  source event: <code className="font-mono text-[10px]">{resyncResult.sourceEventId}</code>
                </p>
              )}
            </div>
            <button
              onClick={() => setResyncResult(null)}
              className="text-muted-foreground hover:text-foreground text-[10px]"
            >
              dismiss
            </button>
          </div>
        </GlassCard>
      )}

      <GlassCard className="p-5 mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <SourceFilterChip
              label="All"
              active={sourceFilter === 'all'}
              onClick={() => setSourceFilter('all')}
              count={visibleRows.length}
            />
            <SourceFilterChip
              label="Local"
              active={sourceFilter === 'local'}
              onClick={() => setSourceFilter('local')}
              count={sourceCounts.local}
            />
            <SourceFilterChip
              label="FOCTTA Core"
              active={sourceFilter === 'foctta_core'}
              onClick={() => setSourceFilter('foctta_core')}
              count={sourceCounts.foctta_core}
            />
            <SourceFilterChip
              label="System"
              active={sourceFilter === 'system'}
              onClick={() => setSourceFilter('system')}
              count={sourceCounts.system}
            />
            <Button
              variant="glass"
              size="sm"
              onClick={() => setShowArchived((v) => !v)}
              className="ml-2"
            >
              {showArchived ? 'Hide archived' : 'Show archived'}
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-action="disc.adm_bus.a04"
              variant="glass"
              size="sm"
              onClick={handleResync}
              disabled={busy}
              title="Pull new business units from your FOCTTA core bootstrap (additive only - never overwrites local edits)"
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              Re-sync from FOCTTA core
            </Button>
            <Button onClick={() => setCreateOpen(true)} size="sm" disabled={busy}>
              <Plus className="h-3 w-3" />
              New business unit
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> loading business units…
          </div>
        ) : visibleRows.length === 0 ? (
          <EmptyState
            sourceFilter={sourceFilter}
            onCreate={() => setCreateOpen(true)}
          />
        ) : (
          <div className="space-y-1.5">
            {visibleRows.map((bu, i) => (
              <motion.div
                key={bu.id}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.02, 0.4) }}
              >
                <BusinessUnitRow
                  bu={bu}
                  parentName={bu.parentId ? nameById.get(bu.parentId) ?? null : null}
                  onEdit={() => startEdit(bu)}
                  onArchive={() => handleArchive(bu)}
                  busy={busy}
                />
              </motion.div>
            ))}
          </div>
        )}
      </GlassCard>

      <CreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        input={createInput}
        setInput={setCreateInput}
        parents={businessUnits.filter((b) => b.status === 'active' && b.source !== 'system')}
        busy={busy}
        onSubmit={handleCreate}
      />

      <EditDialog
        target={editTarget}
        input={editInput}
        setInput={setEditInput}
        parents={businessUnits.filter(
          (b) =>
            b.status === 'active' &&
            b.source !== 'system' &&
            b.id !== editTarget?.id,
        )}
        busy={busy}
        onClose={() => {
          setEditTarget(null);
          setEditInput({});
        }}
        onSubmit={handleUpdate}
      />
    </PageContainer>
  );
}

function SourceFilterChip({
  label,
  active,
  onClick,
  count,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  count: number;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      className={
        active
          ? 'text-xs px-2.5 py-1 rounded-md bg-primary/15 text-foreground border border-primary/40 transition-colors'
          : 'text-xs px-2.5 py-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors'
      }
    >
      {label} <span className="text-[10px] text-muted-foreground">({count})</span>
    </button>
  );
}

function SourceBadge({ source }: { source: BusinessUnitSource }): JSX.Element {
  if (source === 'foctta_core') {
    return (
      <Badge variant="info" className="text-[10px] gap-1">
        <LinkIcon className="h-3 w-3" />
        FOCTTA Core
      </Badge>
    );
  }
  if (source === 'system') {
    return (
      <Badge variant="warning" className="text-[10px] gap-1">
        <ShieldCheck className="h-3 w-3" />
        System
      </Badge>
    );
  }
  return (
    <Badge variant="glass" className="text-[10px] gap-1">
      <Home className="h-3 w-3" />
      Local
    </Badge>
  );
}

function BusinessUnitRow({
  bu,
  parentName,
  onEdit,
  onArchive,
  busy,
}: {
  bu: BusinessUnit;
  parentName: string | null;
  onEdit: () => void;
  onArchive: () => void;
  busy: boolean;
}): JSX.Element {
  const isSystem = bu.status === 'system' || bu.source === 'system';
  const isArchived = bu.status === 'archived';
  return (
    <div
      className={
        'glass-lite rounded-md p-3 flex items-center gap-3 ' +
        (isArchived ? 'opacity-60' : '')
      }
    >
      <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium truncate">{bu.name}</span>
          <SourceBadge source={bu.source} />
          {isArchived && (
            <Badge variant="glass" className="text-[10px] gap-1">
              <ArchiveIcon className="h-3 w-3" />
              Archived
            </Badge>
          )}
          {parentName && (
            <span className="text-[10px] text-muted-foreground">
              under <span className="font-mono">{parentName}</span>
            </span>
          )}
        </div>
        {bu.industry && !isSystem && <IndustryLine industry={bu.industry} parentName={parentName} />}
        <div className="text-[10px] text-muted-foreground flex items-center gap-2 mt-0.5">
          <code className="font-mono">{bu.slug}</code>
          {bu.description && (
            <>
              <span className="opacity-30">·</span>
              <span className="truncate max-w-md">{bu.description}</span>
            </>
          )}
          {bu.externalRef && (
            <>
              <span className="opacity-30">·</span>
              <span title={bu.externalRef}>
                core id <code className="font-mono">{bu.externalRef.slice(0, 8)}…</code>
              </span>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button
          onClick={onEdit}
          variant="glass"
          size="sm"
          disabled={busy || isSystem}
          title={isSystem ? 'System business units cannot be edited' : 'Edit'}
        >
          <Pencil className="h-3 w-3" />
        </Button>
        <Button
          data-action="disc.adm_bus.a03"
          onClick={onArchive}
          variant="glass"
          size="sm"
          disabled={busy || isSystem || isArchived}
          title={
            isSystem
              ? 'System business units cannot be archived'
              : isArchived
                ? 'Already archived'
                : 'Archive'
          }
        >
          <ArchiveIcon className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

/**
 * The unit's industry on its row: the Discovery packs in force, each saying
 * whether it came from the unit's industry in the consent console or was added
 * here, plus its regulatory bundles. Nothing here was visible before: a unit
 * synced from consent as a bank looked exactly like an unconfigured one.
 */
function IndustryLine({
  industry,
  parentName,
}: {
  industry: NonNullable<BusinessUnit['industry']>;
  parentName: string | null;
}): JSX.Element {
  const consentIds = industry.unitPacks.filter((p) => p.source === 'foctta_core').map((p) => p.id);
  return (
    <div className="mt-1 space-y-1" data-testid="bu-industry">
      <div className="flex items-center gap-1 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1">Industry</span>
        {industry.packs.length > 0 ? (
          <PackOriginChips packs={industry.packs} />
        ) : (
          <span className="text-[10px] text-muted-foreground">none - Tier 1 detectors only</span>
        )}
        <BundleChips bundles={industry.bundles} />
      </div>
      <div className="text-[10px] text-muted-foreground">
        {resolutionSentence({
          mode: industry.mode,
          inheritedFromName: industry.mode === 'inherited' ? parentName : null,
          fallThroughReason: industry.fallThroughReason,
          packCount: industry.packs.length,
        })}
        {consentIds.length > 0 && (
          <> Consent industry: {consentIds.map(humanisePackId).join(', ')}.</>
        )}
        {industry.unmapped.length > 0 && (
          <span className="text-amber-700 dark:text-amber-300">
            {' '}No classification mapping for: {industry.unmapped.join(', ')}.
          </span>
        )}
      </div>
    </div>
  );
}

function EmptyState({
  sourceFilter,
  onCreate,
}: {
  sourceFilter: SourceFilter;
  onCreate: () => void;
}): JSX.Element {
  return (
    <div className="text-center py-12 text-muted-foreground">
      <Building2 className="h-8 w-8 mx-auto mb-3 opacity-40" />
      <p className="text-sm">
        {sourceFilter === 'all'
          ? 'No business units yet.'
          : `No ${sourceFilter === 'foctta_core' ? 'FOCTTA core' : sourceFilter} business units.`}
      </p>
      <p className="text-[11px] mt-1">
        {sourceFilter === 'foctta_core'
          ? 'Connect FOCTTA core under Settings → Integrations to bootstrap BUs from your privacy tenant.'
          : 'Create one to start scoping findings, events, and decisions.'}
      </p>
      {sourceFilter !== 'foctta_core' && sourceFilter !== 'system' && (
        <Button onClick={onCreate} className="mt-4" size="sm">
          <Plus className="h-3 w-3" />
          Create the first business unit
        </Button>
      )}
    </div>
  );
}

function CreateDialog({
  open,
  onOpenChange,
  input,
  setInput,
  parents,
  busy,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  input: CreateBusinessUnitInput;
  setInput: (v: CreateBusinessUnitInput) => void;
  parents: BusinessUnit[];
  busy: boolean;
  onSubmit: () => void;
}): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New business unit</DialogTitle>
          <DialogDescription>
            Slug is auto-derived from the name when left blank. Parent is optional - leave it as
            "(no parent - top-level)" to create a root BU.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <FormField
            label="Name"
            value={input.name}
            onChange={(v) => setInput({ ...input, name: v })}
            placeholder="e.g. Wealth Management India"
            required
          />
          <FormField
            label="Slug (optional)"
            value={input.slug ?? ''}
            onChange={(v) => setInput({ ...input, slug: v })}
            placeholder="auto-derived from name"
            mono
          />
          <ParentSelector
            value={input.parentId ?? null}
            onChange={(v) => setInput({ ...input, parentId: v })}
            parents={parents}
          />
          <FormField
            label="Description (optional)"
            value={input.description ?? ''}
            onChange={(v) => setInput({ ...input, description: v })}
            placeholder="Short description of the BU's scope"
            textarea
          />
        </div>
        <div className="flex gap-2 mt-4">
          <Button data-action="disc.adm_bus.a01" onClick={onSubmit} disabled={busy || !input.name.trim()}>
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
            Create
          </Button>
          <Button onClick={() => onOpenChange(false)} variant="glass" disabled={busy}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({
  target,
  input,
  setInput,
  parents,
  busy,
  onClose,
  onSubmit,
}: {
  target: BusinessUnit | null;
  input: UpdateBusinessUnitInput;
  setInput: (v: UpdateBusinessUnitInput) => void;
  parents: BusinessUnit[];
  busy: boolean;
  onClose: () => void;
  onSubmit: () => void;
}): JSX.Element {
  const open = target != null;
  const isFocttaCore = target?.source === 'foctta_core';
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit business unit</DialogTitle>
          <DialogDescription>
            {isFocttaCore ? (
              <span className="flex items-start gap-1.5">
                <AlertTriangle className="h-3 w-3 text-warning mt-0.5 shrink-0" />
                <span>
                  This BU was imported from FOCTTA core. You can edit it locally without affecting
                  the source - re-sync is additive only and won't overwrite your changes.
                </span>
              </span>
            ) : (
              'Edit fields. Slug must remain unique within your tenant.'
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <FormField
            label="Name"
            value={input.name ?? ''}
            onChange={(v) => setInput({ ...input, name: v })}
            placeholder="Business unit name"
          />
          <FormField
            label="Slug"
            value={input.slug ?? ''}
            onChange={(v) => setInput({ ...input, slug: v })}
            mono
          />
          <ParentSelector
            value={input.parentId ?? null}
            onChange={(v) => setInput({ ...input, parentId: v })}
            parents={parents}
          />
          <FormField
            label="Description"
            value={input.description ?? ''}
            onChange={(v) => setInput({ ...input, description: v })}
            textarea
          />
        </div>
        <div className="flex gap-2 mt-4">
          <Button data-action="disc.adm_bus.a02" onClick={onSubmit} disabled={busy}>
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
            Save changes
          </Button>
          <Button onClick={onClose} variant="glass" disabled={busy}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ParentSelector({
  value,
  onChange,
  parents,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
  parents: BusinessUnit[];
}): JSX.Element {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">
        Parent business unit
      </label>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        className="w-full glass-lite rounded-md p-2 text-xs outline-none"
      >
        <option value="">(no parent - top-level)</option>
        {parents.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name} ({p.slug})
          </option>
        ))}
      </select>
    </div>
  );
}

function FormField({
  label,
  value,
  onChange,
  placeholder,
  required,
  mono,
  textarea,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
  mono?: boolean;
  textarea?: boolean;
}): JSX.Element {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </label>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={2}
          className={
            'w-full glass-lite rounded-md p-2 text-xs outline-none resize-none ' +
            (mono ? 'font-mono' : '')
          }
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={
            'w-full glass-lite rounded-md p-2 text-xs outline-none ' +
            (mono ? 'font-mono' : '')
          }
        />
      )}
    </div>
  );
}
